import { combineReducers } from 'redux';
import axios from 'axios';
import humps from 'humps';
import { getFormValues, getFormSyncErrors, change as formChange } from 'redux-form';
import { createAction } from 'redux-actions';
import _ from 'lodash';
import qs from 'qs';

import * as Urls from 'src/constants/EndpointUrls';
import { concatParamsToUrl, redirectTo } from 'src/utils/Http';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { editItem, editItemFailure, getItemErrors } from 'src/reducers/common/itemReducer';
import {
  fetchList,
  fetchListSuccess,
  fetchListFailure,
  updateOne,
  getExtras,
  setExtras
} from 'src/reducers/common/listReducer';
import { setGlobalSuccesses, setGlobalErrors } from 'src/reducers/global';

export const REDUCER_NAME = 'notificationOfBaseAmountRevisions';

export const UI_SET = 'REVISIONS_UI/SET';
export const UI_CHANGE_SHOW_MODE = 'REVISIONS_UI/CHANGE_SHOW_MODE';
export const UI_CHANGE_CALC_MODE = 'REVISIONS_UI/UI_CHANGE_CALC_MODE';
export const UI_NOTIFY_VALUE_CHANGES = 'REVISIONS_UI/UI_NOTIFY_VALUE_CHANGES';
export const UI_RESET_VALUE_CHANGES = 'REVISIONS_UI/UI_RESET_VALUE_CHANGES';

export const uiSet = createAction(UI_SET);
export const uiChangeShowMode = createAction(UI_CHANGE_SHOW_MODE);
export const uiChangeCalcMode = createAction(UI_CHANGE_CALC_MODE);
export const uiNotifyValueChanges = createAction(UI_NOTIFY_VALUE_CHANGES);
export const uiResetValueChanges = createAction(UI_RESET_VALUE_CHANGES);

export const getUiShowMode = (state, revision) => _.get(state, `monthlyRevisions.ui.revisions.${revision.id}.showMode`);
export const getUiCalcMode = (state, revision) => _.get(state, `monthlyRevisions.ui.revisions.${revision.id}.calcMode`);
export const getReadyForRecalculating = (state, revision) =>
  _.get(state, `monthlyRevisions.ui.revisions.${revision.id}.readyForRecalculating`);
export const getChangedYearMonths = state => _.get(state, 'monthlyRevisions.ui.changedYearMonths');
export const getLastRevisionYearMonths = state => _.get(getExtras(REDUCER_NAME, state), 'lastRevisionYearMonths');
export const getUnconfirmedAlertCount = state => _.get(getExtras(REDUCER_NAME, state), 'unconfirmedAlertCount');
export const getRevisionErrors = state => getItemErrors(REDUCER_NAME, state) || [{}];

export const transformRevisionsToFormValues = (reducerName, state) =>
  state[reducerName].list.data.reduce(
    (pre, revision) => ({ revisions: { ...pre.revisions, [revision.id]: revision } }),
    { revisions: {} }
  );

export const getConfirmRevisionErrors = state => {
  const errors = getItemErrors(REDUCER_NAME, state) || [{}];
  return (errors[0] || {}).changeNotificationOfBaseAmountErrors || [];
};

// Async Action Creators
export const fetchRevisions = queries => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));

    const response = await axios.get(Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_SEARCH, {
      params: humps.decamelizeKeys(queries)
    });
    const payload = {
      data: response.data.payload.revisions,
      pageCount: response.data.payload.totalPages,
      totalCount: response.data.payload.totalCount,
      targetStartDate: response.data.payload.targetStartDate,
      targetEndDate: response.data.payload.targetEndDate
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(REDUCER_NAME, { changeStandardMonthlyDate: response.data.payload.changeStandardMonthlyDate }));
    dispatch(setExtras(REDUCER_NAME, { targetPayDate: response.data.payload.targetPayDate }));
    dispatch(setExtras(REDUCER_NAME, { targetCount: response.data.payload.targetCount }));
    dispatch(setExtras(REDUCER_NAME, { lastRevisionYearMonths: response.data.payload.lastRevisionYearMonths }));
    dispatch(setExtras(REDUCER_NAME, { unconfirmedAlertCount: response.data.payload.unconfirmedAlertCount }));

    dispatch(
      uiSet({ revisions: response.data.payload.revisions, changedYearMonths: response.data.payload.changedYearMonths })
    );
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const editRevision = revisionId => async (dispatch, getState) => {
  try {
    const state = getState();
    const revision = _.get(getFormValues('revisions')(state), `revisions.${revisionId}`);

    // エラーがあるならスキップ
    if (_.get(getFormSyncErrors('revisions')(state), `revisions.${revisionId}`)) {
      return;
    }

    // modifiedAverageAmount は変更されていなければ送信しない。
    if (getUiCalcMode(state, revision) === 'AUTO') {
      delete revision.modifiedAverageAmount;
    }

    const httpBody = {
      revision
    };

    const updatePayUrl = concatParamsToUrl(Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_UPDATE, { id: revision.id });
    const response = await axios.put(updatePayUrl, httpBody);

    dispatch(formChange('revisions', `revisions.${revisionId}`, response.data.payload.revision));
    dispatch(updateOne(REDUCER_NAME, { id: revisionId, item: response.data.payload.revision }));
    dispatch(uiChangeShowMode({ revision: response.data.payload.revision, showMode: 'SHOW' }));

    dispatch(uiChangeCalcMode({ revision, calcMode: 'AUTO' }));
    dispatch(uiResetValueChanges({ revision }));
    dispatch(setExtras(REDUCER_NAME, { unconfirmedAlertCount: response.data.payload.unconfirmedAlertCount }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const recalculateRevision = (revisionId, autoCalc) => async (dispatch, getState) => {
  try {
    const state = getState();
    const revision = _.get(getFormValues('revisions')(state), `revisions.${revisionId}`);

    // エラーがあるならスキップ
    if (_.get(getFormSyncErrors('revisions')(state), `revisions.${revisionId}`)) {
      return;
    }

    if (!getReadyForRecalculating(state, revision)) {
      return;
    }

    const calcMode = autoCalc ? 'AUTO' : 'MANUAL';
    dispatch(uiChangeCalcMode({ revision, calcMode }));

    // modifiedAverageAmount は変更されていなければ送信しない。
    if (getUiCalcMode(state, revision) === 'AUTO') {
      delete revision.modifiedAverageAmount;
    }

    const httpBody = {
      revision
    };

    const updatePayUrl = concatParamsToUrl(Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_RECALCULATE, { id: revision.id });
    const response = await axios.post(updatePayUrl, httpBody);

    dispatch(formChange('revisions', `revisions.${revisionId}`, response.data.payload.revision));

    dispatch(uiResetValueChanges({ revision }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const resetRevision = revisionId => async (dispatch, getState) => {
  try {
    const state = getState();
    const revision = _.get(getFormValues('revisions')(state), `revisions.${revisionId}`);

    const updatePayUrl = concatParamsToUrl(Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_RESET, {
      id: revisionId
    });

    const httpBody = {
      revision_year: revision.revisionYear
    };

    const response = await axios.post(updatePayUrl, httpBody);

    dispatch(formChange('revisions', `revisions.${revisionId}`, response.data.payload.revision));

    dispatch(uiChangeCalcMode({ revision, calcMode: 'AUTO' }));
    dispatch(uiResetValueChanges({ revision }));
    dispatch(setExtras(REDUCER_NAME, { unconfirmedAlertCount: response.data.payload.unconfirmedAlertCount }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const toggleSubjectRevision = (revisionId, isForceSubject) => async dispatch => {
  try {
    const toggleSubjectUrl = concatParamsToUrl(Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_TOGGLE_SUBJECT, {
      id: revisionId
    });

    const httpBody = {
      isForceSubject
    };

    await axios.post(toggleSubjectUrl, httpBody);

    window.location.reload();
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const changeNotificationOfBaseAmountConfirm = (revisionYear, callBack) => async (dispatch, getState) => {
  const revisionIds = [];
  const revisions = getFormValues('revisions')(getState()).revisions;
  _.forEach(revisions, revision => {
    if (revision.displaySubjectFlag) revisionIds.push(revision.id);
  });

  try {
    const httpBody = humps.decamelizeKeys({ revision_year: revisionYear, revision_ids: revisionIds });
    const response = await axios.put(Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_CONFIRM, httpBody);
    if (response.status === 200) {
      dispatch(setGlobalSuccesses('標準報酬月額を改定しました'));
    } else {
      dispatch(setGlobalErrors('標準報酬月額の改定処理が失敗しました'));
    }
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  } finally {
    callBack();
  }
};

export const downloadCsv = (data, revisionYear, callBack) => async dispatch => {
  try {
    dispatch(editItem(REDUCER_NAME));
    const params = {
      payment_for: data.payment_for,
      serial_number: data.serial_number,
      business_owner_last_name: data.business_owner_last_name,
      business_owner_first_name: data.business_owner_first_name,
      revisionYear
    };
    const query = qs.stringify(humps.decamelizeKeys(params), { arrayFormat: 'brackets' });
    const url = `${Urls.NOTIFICATION_OF_BASE_AMOUNT_REVISION_CSV}?${query}`;
    // csv作成時のエラーをキャッチするために一度axiosのgetを実行している
    await axios.get(url);
    redirectTo(url);
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  } finally {
    callBack();
  }
};

const uiReducer = (state = { revisions: {}, changedYearMonths: [] }, action) => {
  switch (action.type) {
    case UI_SET:
      return {
        changedYear: action.payload.changedYear,
        revisions: action.payload.revisions.reduce(
          (pre, revision) => ({
            ...pre,
            [revision.id]: { showMode: 'SHOW', calcMode: 'AUTO', readyForRecalculating: false }
          }),
          {}
        )
      };
    case UI_CHANGE_SHOW_MODE:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: {
            ...state.revisions[action.payload.revision.id],
            showMode: action.payload.showMode
          }
        }
      };
    case UI_CHANGE_CALC_MODE:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: {
            ...state.revisions[action.payload.revision.id],
            calcMode: action.payload.calcMode
          }
        }
      };
    case UI_NOTIFY_VALUE_CHANGES:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: { ...state.revisions[action.payload.revision.id], readyForRecalculating: true }
        }
      };
    case UI_RESET_VALUE_CHANGES:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: { ...state.revisions[action.payload.revision.id], readyForRecalculating: false }
        }
      };
    default:
      return state;
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME),
  ui: uiReducer
});
